<template>
  <div class="widget w-constituents-widget" :class="['font-size-' + fontSize, { bold: isFontBold }]">
    <c-widget-header
      csvExport
      fieldsConfigManager="advanced"
      optimizeColumns
      performanceBar
      printView
      :wid="wid"
      @csv-export="onCsvExport"
      @optimize-columns="autoSizeColumns"
    >
      <c-incremental-search
        :autocompleteInstruments="['STO', 'IND']"
        slot="left"
        :ric="ric"
        @error="onSymbolSearchError"
        @change-ric="setSymbol"
      />
      <slot name="buttons" slot="buttons" />
    </c-widget-header>

    <!-- Widget Menu -->
    <c-widget-control-bar v-if="ric">
      <v-form-group :label="i18n('template', 'globalLabels')" label-cols="auto">
        <v-form-select
          :aria-label="i18n('template', 'globalLabels')"
          :options="fieldsConfigNameOptions"
          v-model="controlFieldsConfigName"
        />
      </v-form-group>
      <v-form-group :label="i18n('autosort', 'globalLabels')" label-cols="auto">
        <c-widget-input-switch :options="autosortOptions" v-model="controlAutosort" />
      </v-form-group>
    </c-widget-control-bar>

    <div v-if="ric" class="widget-body">
      <c-advances-declines
        v-if="showPerformanceBar && !isError"
        :label="$t('white-label.widgets.portfolio.labels.advancesDeclines')"
        :advances="performanceBar.advances"
        :declines="performanceBar.declines"
      />
      <c-loader v-if="isLoading" />
      <ag-grid-vue
        v-if="!isError"
        v-show="!isLoading"
        class="c-data-grid ag-theme-balham-dark"
        animateRows
        :asyncTransactionWaitMillis="gridUpdateInterval"
        suppressContextMenu
        :columnDefs="columnDefs"
        :context="this"
        :defaultColDef="defaultColumnDef"
        ensureDomOrder
        :gridOptions="gridOptions"
        immutableData
        rowSelection="single"
        :rowHeight="agGridRowHeight"
        suppressFieldDotNotation
        :suppressColumnVirtualisation="isAccessibilityEnabled"
        @grid-ready="onGridReady"
        @row-selected="updateLink"
        @sort-changed="onTableSort"
        @cell-focused="onCellFocused"
        @async-transactions-flushed="onAsyncTransactionsFlushed"
      />
    </div>
    <v-alert v-else align="center" class="m-2" show variant="light">{{ i18n('nosymbol', 'globalMessages') }}</v-alert>
    <b-toaster :name="notifierId"></b-toaster>
  </div>
</template>

<script>
import CAdvancesDeclines from '@custom/CAdvancesDeclines';
import CLoader from '@custom/CLoader';
import CWidgetControlBar from '@custom/widgetContainer/CWidgetControlBar';
import CWidgetHeader from '@custom/widgetContainer/CWidgetHeader';
import CWidgetHeaderButton from '@custom/widgetContainer/CWidgetHeaderButton';
import CIncrementalSearch from '@custom/CIncrementalSearch';
import { getConsolidatedRic } from '@/utils/helpers';

import { API } from '@/state/api';
import { AgGridVue } from 'ag-grid-vue';
import agGridCommons from '@mixins/agGridCommons.js';

export default {
  name: 'w-constituents-widget',
  mixins: [agGridCommons],
  components: {
    CAdvancesDeclines,
    CLoader,
    CWidgetControlBar,
    CWidgetHeader,
    CWidgetHeaderButton,
    CIncrementalSearch,
    AgGridVue
  },
  props: {
    wid: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      formats: {},
      isLoading: true,
      isError: false,
      // --------------------------
      gridOptions: {
        getRowNodeId(data) {
          return data['q.RIC'];
        },
        columnTypes: { actionable: {} },
        tabToNextHeader: this.tabToNextHeader,
        tabToNextCell: this.tabToNextCell
      }
    };
  },
  computed: {
    ric() {
      return this.widgetStore.ric;
    }
  },
  watch: {
    ric() {
      this.fetch();
    }
  },
  methods: {
    setSymbol(ric) {
      if (ric !== this.ric) {
        this.$store.commit(`widget/${this.wid}/RIC_CHANGED`, String(ric));
        this.isLoading = true;
        this.isError = false;
      }
    },
    getIndexConstituents() {
      return API.Rest.get(`/index/constituents?ric=${getConsolidatedRic(this.ric)}&fids=${this.requestFids}`).then(
        response => {
          if (response.data.data.length === 0) {
            throw new Error('NO_CONSTITUENTS_RECEIVED');
          }

          Object.assign(
            this.formats,
            Object.keys(response.data.metadata).reduce((acc, ric) => {
              acc[ric] = response.data.metadata[ric].type;
              return acc;
            }, {})
          );
          // reset net change collection to prevent holding obsolete values
          this.netChanges = {};

          const snapshot = response.data.data;
          const rowsData = [];
          snapshot.forEach(data => {
            const row = this.gridOptions.api.getRowNode(data['q.RIC']);
            rowsData.push(
              this.hydrateData(
                data,
                // clone row data object, otherwise ag-grid won't detect if there were any changes (must be new object reference)
                row ? { ...row.data } : {}
              )
            );
          });
          this.gridOptions.api.setRowData(rowsData);
          this.autoSizeColumns();
          this.isLoading = false;
          return response.data;
        }
      );
    },
    handleUpdate(response, error) {
      if (error) {
        console.error('Streaming update', error);
        return;
      }
      response = response.payload[0];
      let rowNode;
      try {
        rowNode = this.gridOptions.api.getRowNode(response.ric);
      } catch (e) {
        return;
      }
      // if there's no rowNode, then there's most probalby a running refetch, so don't try to update removed rows
      if (!rowNode) return;
      const updateData = this.hydrateData(response.data, rowNode.data);
      if (this.gridOptions && this.gridOptions.api)
        this.gridOptions.api.applyTransactionAsync({ update: [updateData] });
    },
    fetch() {
      this.isLoading = true;
      this.streamingUnsubscribe();
      // reset table on re-fetch to avoid cell renderers to attempt rendering before the data arrives
      if (this.gridOptions.api) this.gridOptions.api.setRowData([]);
      this.getIndexConstituents()
        .then(response => this.streamingSubscribe(response.data.map(data => data['q.RIC'])))
        .catch(e => {
          console.log(e);
          this.isLoading = false;
          this.isError = true;
          this.$notify.error(
            this.i18n('error.noResponse.head'),
            this.i18n('error.noResponse.label', { ric: this.ric }),
            {
              group: this.notifierId,
              duration: -1,
              closeButton: true
            }
          );
        });
    },
    onGridReady(ev) {
      this.onTableReady(ev, this.i18n('widget-title'));
    }
  },
  // Life-cycle
  mounted() {
    if (this.ric) this.fetch();
  },
  beforeDestroy() {
    this.streamingUnsubscribe();
    if (this.gridOptions) this.gridOptions = null;
    if (this.gridApi) this.gridApi = null;
  }
};
</script>

<style lang="scss">
@import '~@/styles/widgets/constituents-widget';
</style>
